import React, { useEffect, useRef, useState } from 'react';
import ClayTaxi from './ClayTaxi';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';

export const TIMELINE_EVENT = 'timeline-animation';
const LERP = 0.15;
const CAR_HEIGHT = 47;

// Desktop-only scroll indicator. The dashed road is fixed and never changes;
// only the taxi moves, easing toward the scroll position each frame so it
// glides instead of jumping. While the "How a booking works" timeline plays
// its own car animation, the road steps out of the way (see HowItWorks).
function ScrollRoad() {
  const reducedMotion = usePrefersReducedMotion();
  const trackRef = useRef(null);
  const carRef = useRef(null);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    const onTimeline = (e) => setPaused(Boolean(e.detail?.running));
    window.addEventListener(TIMELINE_EVENT, onTimeline);
    return () => window.removeEventListener(TIMELINE_EVENT, onTimeline);
  }, []);

  useEffect(() => {
    let frame = 0;
    let current = null;

    const target = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const progress = max > 0 ? Math.min(Math.max(window.scrollY / max, 0), 1) : 0;
      const travel = (trackRef.current?.offsetHeight || 0) - CAR_HEIGHT;
      return progress * Math.max(travel, 0);
    };

    const step = () => {
      frame = 0;
      const goal = target();
      if (current === null || reducedMotion) current = goal;
      else current += (goal - current) * LERP;
      if (Math.abs(goal - current) < 0.5) current = goal;
      if (carRef.current) carRef.current.style.transform = `translate3d(-50%, ${current}px, 0)`;
      if (current !== goal) frame = requestAnimationFrame(step);
    };

    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(step);
    };

    step();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [reducedMotion]);

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none fixed bottom-[104px] left-1 top-[96px] z-road hidden w-10 transition-opacity duration-[250ms] ease-out motion-reduce:transition-none lg:block ${
        paused ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <div ref={trackRef} className="road-dash absolute bottom-0 left-1/2 top-0 w-1 -translate-x-1/2 rounded-full opacity-50 [--road-dir:bottom]" />
      <div ref={carRef} className="absolute left-1/2 top-0 will-change-transform" style={{ transform: 'translate3d(-50%, 0, 0)' }}>
        <ClayTaxi size={36} />
      </div>
    </div>
  );
}

export default ScrollRoad;
